import { useTranslation } from '@/hooks/use-translation'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SurveyUiTemplate } from '@/features/forms-and-surveys/types'

interface RatingSettingsProps {
  minValue: number
  maxValue: number
  uiTemplate: SurveyUiTemplate
  onMinValueChange: (value: number) => void
  onMaxValueChange: (value: number) => void
  onUiTemplateChange: (template: SurveyUiTemplate) => void
  minValueError?: string
  maxValueError?: string
}

export function RatingSettings({
  minValue,
  maxValue,
  uiTemplate,
  onMinValueChange,
  onMaxValueChange,
  onUiTemplateChange,
  minValueError,
  maxValueError,
}: RatingSettingsProps) {
  const { t } = useTranslation()

  const uiTemplateOptions = [
    { value: 'Default', label: t('surveys.default') },
    { value: 'Stars', label: t('surveys.stars') },
    { value: 'Faces', label: t('surveys.faces') },
    { value: 'Emoji', label: t('surveys.emoji') },
    { value: 'Scale', label: t('surveys.scale') },
  ]

  return (
    <div className='space-y-4'>
      <Label>{t('surveys.ratingRange')}</Label>

      <div className='grid grid-cols-2 gap-4'>
        <div className='space-y-2'>
          <Label htmlFor='min-value'>{t('surveys.minimumValue')}</Label>
          <Input
            id='min-value'
            type='number'
            value={minValue}
            onChange={(e) => onMinValueChange(Number(e.target.value))}
            min={0}
            max={maxValue - 1}
            className={minValueError ? 'border-red-500' : ''}
          />
          {minValueError && (
            <p className='text-sm text-red-600'>{minValueError}</p>
          )}
        </div>

        <div className='space-y-2'>
          <Label htmlFor='max-value'>{t('surveys.maximumValue')}</Label>
          <Input
            id='max-value'
            type='number'
            value={maxValue}
            onChange={(e) => onMaxValueChange(Number(e.target.value))}
            min={minValue + 1}
            max={10}
            className={maxValueError ? 'border-red-500' : ''}
          />
          {maxValueError && (
            <p className='text-sm text-red-600'>{maxValueError}</p>
          )}
        </div>
      </div>

      <div className='space-y-2'>
        <Label htmlFor='ui-template'>{t('surveys.displayStyle')}</Label>
        <Select value={uiTemplate} onValueChange={onUiTemplateChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {uiTemplateOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className='text-sm text-muted-foreground'>
        <p>
          {t('surveys.ratingRangeDescription', {
            min: minValue,
            max: maxValue,
            count: maxValue - minValue + 1,
          })}
        </p>
      </div>
    </div>
  )
}
